import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import App from "@/App";
import { enDocs, enMeet, esRutaPublicaDeDocs, puedeVivirEnDocs, puedeVivirEnMeet, volverAlCorreo } from "@/lib/dominios";
import { CLAVES_SESION, conectarSesionCorreo } from "@/lib/sesionCorreo";
import { useStore } from "@/store/useStore";

// Silence the benign "ResizeObserver loop completed with undelivered notifications"
// warning so it doesn't trigger the dev error overlay.
const _roErr = "ResizeObserver loop";
window.addEventListener("error", (e) => {
  if (e.message && e.message.includes(_roErr)) {
    e.stopImmediatePropagation();
    e.preventDefault();
  }
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

function leerUsuario() {
  try {
    return JSON.parse(localStorage.getItem("zuno_user") || "null");
  } catch {
    return null;
  }
}

function pintar() {
  const root = ReactDOM.createRoot(document.getElementById("root"));
  root.render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </React.StrictMode>,
  );
}

// docs.orvian.mx (documentos, hojas y presentaciones): antes de pintar se trae la sesión del
// correo (lib/sesionCorreo.js). El store ya leyó el localStorage de docs al importarse, así que
// se le pone al día con lo que llegó.
async function arrancarEnDocs() {
  if (!puedeVivirEnDocs(window.location.pathname)) {
    volverAlCorreo(); // Drive, el correo, el acceso… no son de este dominio
    return;
  }
  let incrustado = false;
  try {
    incrustado = window.self !== window.top;
  } catch {
    incrustado = true;
  }
  const publica = esRutaPublicaDeDocs(window.location.pathname);
  // Un formulario incrustado en otra web no puede recibir la sesión (el navegador aísla el iframe
  // del correo y nginx solo deja enmarcarlo desde docs): se responde sin cuenta, sin esperar al puente.
  // Responder no necesita sesión, así que ahí se espera poco.
  if (incrustado && publica) {
    try {
      CLAVES_SESION.forEach((clave) => localStorage.removeItem(clave)); // nada de una sesión vieja reflejada
    } catch {
      /* sin almacenamiento */
    }
    useStore.setState({ user: null });
    pintar();
    return;
  }
  await conectarSesionCorreo({
    espera: publica ? 2500 : 8000,
    alCambiar: (clave, valor) => {
      if (clave === "zuno_theme") useStore.setState({ theme: valor || "light" });
      // Cerró sesión en el correo: aquí también (Protected lo manda a entrar, en el correo)
      if ((clave === "zuno_user" || clave === null) && !leerUsuario()) useStore.setState({ user: null });
    },
  });
  useStore.setState({ user: leerUsuario(), theme: localStorage.getItem("zuno_theme") || "light" });
  pintar();
}

// meet.orvian.mx (Orvian Meet): igual que docs, la sesión la trae del correo. Sin sesión también se
// puede usar (unirse como invitado), así que si el correo no contesta se pinta igual, sin cuenta.
async function arrancarEnMeet() {
  // meet.orvian.mx/login (dirección que el navegador guardó de la versión anterior): el inicio de Meet
  // ya trae la sesión del correo, así que se va ahí en vez de mandar al formulario de acceso.
  if (/^\/login\/?$/i.test(window.location.pathname)) window.history.replaceState(null, "", "/");
  if (!puedeVivirEnMeet(window.location.pathname)) {
    volverAlCorreo(); // el correo, Drive, el acceso… no son de este dominio
    return;
  }
  // Dentro de la app Orvian Mail (vista web): la sesión llega en el fragmento (#t=<access>, no viaja al servidor) y
  // no se pide al correo (en la vista web no hay sesión del correo que traer). Ver app/lib/screens/meet_sala_screen.dart.
  const fragmento = new URLSearchParams((window.location.hash || "").replace(/^#/, ""));
  if (fragmento.get("t")) {
    localStorage.setItem("zuno_access", fragmento.get("t"));
    sessionStorage.setItem("zuno_meet_app", "1");
    window.history.replaceState(null, "", window.location.pathname + window.location.search);
  }
  if (sessionStorage.getItem("zuno_meet_app") === "1") {
    const tema = new URLSearchParams(window.location.search).get("theme");
    useStore.setState({ user: leerUsuario(), theme: tema || localStorage.getItem("zuno_theme") || "light" });
    pintar();
    return;
  }
  await conectarSesionCorreo({
    espera: 5000,
    alCambiar: (clave, valor) => {
      if (clave === "zuno_theme") useStore.setState({ theme: valor || "light" });
      if ((clave === "zuno_user" || clave === null) && !leerUsuario()) useStore.setState({ user: null });
      if (clave === "zuno_user" && leerUsuario()) useStore.setState({ user: leerUsuario() });
    },
  });
  useStore.setState({ user: leerUsuario(), theme: localStorage.getItem("zuno_theme") || "light" });
  pintar();
}

if (enDocs()) arrancarEnDocs();
else if (enMeet()) arrancarEnMeet();
else pintar();

// Register the service worker for offline app-shell caching + web push.
// scope '/' so push works site-wide even when not on /mail.
// En docs y en Meet no: ahí no hay avisos ni correo que guardar sin conexión.
if ("serviceWorker" in navigator && !enDocs() && !enMeet()) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js", { scope: "/" }).catch((err) => {
      console.warn("Service Worker registration failed", err);
    });
  });
}
